import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'folie-install-dismissed';
const ALREADY_INSTALLED_KEY = 'folie-install-already-installed';
const VISIT_COUNT_KEY = 'folie-visit-count';
const MIN_VISITS = 3;

/** Detect iOS Safari (no beforeinstallprompt support) */
function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS|Chrome/.test(ua);
  return isIOS && isSafari;
}

/**
 * Navigateur intégré d'une application (Facebook, Instagram, Messenger…).
 * Le lien d'une publication s'y ouvre, et l'installation y est impossible :
 * ni « Sur l'écran d'accueil » sur iPhone, ni bouton Installer sur Android.
 * Jusqu'ici ces visiteurs ne voyaient AUCUN message.
 */
export function isInAppBrowser(ua: string = navigator.userAgent): boolean {
  return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|Line\/|Snapchat|TikTok|musical_ly|LinkedInApp|Pinterest/i.test(ua);
}

/** Version majeure de Safari (« Version/26.0 ») ; null hors Safari. */
export function safariMajor(ua: string = navigator.userAgent): number | null {
  const m = ua.match(/Version\/(\d+)[.\d]* .*Safari/);
  return m ? Number(m[1]) : null;
}

/** Detect macOS Safari (desktop) */
function isMacSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome/.test(ua) && /Macintosh/.test(ua) && navigator.maxTouchPoints === 0;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [inApp, setInApp] = useState(false);

  useEffect(() => {
    // Check if already running as standalone PWA
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true // iOS Safari standalone check
    ) {
      setIsInstalled(true);
      return;
    }

    // Check if user declared the app as already installed (permanent dismissal)
    if (localStorage.getItem(ALREADY_INSTALLED_KEY) === '1') {
      setIsDismissed(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) {
        setIsDismissed(true);
        return;
      }
    }

    // Track visits
    const visits = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(visits));

    // Navigateur intégré : on le dit dès la PREMIÈRE visite — ces visiteurs
    // arrivent par un lien et reviennent rarement trois fois par ce chemin.
    if (isInAppBrowser()) {
      setInApp(true);
      setCanShow(true);
      return;
    }

    const safari = isIOSSafari() || isMacSafari();
    setIsSafari(safari);

    if (safari) {
      // Safari doesn't support beforeinstallprompt — show manual instructions
      if (visits >= MIN_VISITS) {
        setCanShow(true);
      }
    } else {
      // Chrome, Edge, Firefox on Android — standard beforeinstallprompt
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        if (visits >= MIN_VISITS) {
          setCanShow(true);
        }
      };

      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') {
      setIsInstalled(true);
      return true;
    }
    return false;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setIsDismissed(true);
    setCanShow(false);
  }, []);

  const markAlreadyInstalled = useCallback(() => {
    localStorage.setItem(ALREADY_INSTALLED_KEY, '1');
    setIsDismissed(true);
    setCanShow(false);
  }, []);

  return {
    canShow: canShow && !isInstalled && !isDismissed,
    isInstalled,
    isSafari,
    isIOSSafari: isIOSSafari(),
    inApp,
    install,
    dismiss,
    markAlreadyInstalled,
  };
}
